import { Fragment, useEffect, useState, type CSSProperties } from "react";
import type { GetServerSideProps } from "next";
import Head from "next/head";
import Link from "next/link";
import { Inter, Manrope } from "next/font/google";
import HomeFooter from "../components/homepage/HomeFooter";
import HomeHeader from "../components/homepage/HomeHeader";
import {
  CONTAINER_CLASS,
  FOCUS_RING_CLASS,
} from "../components/homepage/homepageLinks";
import { toHomepageStory } from "../lib/homepage/news";
import {
  HOMEPAGE_CATEGORIES,
  getHomepageCategory,
} from "../lib/homepage/categories";
import type { HomepageStory } from "../lib/homepage/types";
import { fetchBloggerPosts } from "../lib/blogger/posts";
import { bloggerUrlToNativeNewsPath } from "../lib/news/routes";
import NewsImage from "../components/news/NewsImage";
import NewsNewsletterCard from "../components/news/NewsNewsletterCard";
import EditorialFlyerCard from "../components/homepage/EditorialFlyerCard";
import { useCurrentFlyers } from "../hooks/useCurrentFlyers";
import { archiveFlyerAfter } from "../lib/promotions/slots";
import { useShopperLocation } from "../hooks/useShopperLocation";

const manrope = Manrope({
  subsets: ["latin"],
  weight: ["600", "700", "800"],
  variable: "--font-manrope",
  display: "swap",
});

const inter = Inter({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
  variable: "--font-inter",
  display: "swap",
});

const fontVariables = {
  "--font-display": "var(--font-manrope)",
  "--font-body": "var(--font-inter)",
} as CSSProperties;

interface NewsArchiveProps {
  stories: HomepageStory[];
  activeCategory: string | null;
}

const SITE_URL = "https://www.youdle.io";
const PAGE_TITLE = "Grocery News | Youdle";
const PAGE_DESCRIPTION =
  "Grocery news, recalls, price changes, shopper finds and useful stories for your next grocery run.";

const categoryLabels: Record<string, string> = {
  "recalls-warnings": "Recalls",
  "prices-money": "Prices & Savings",
  "grocery-news": "Grocery News",
  "food-health": "Food & Health",
  "meal-planning": "Meal Planning",
  "smart-shopping": "Shopping Tips",
};

const EDITORIAL_HEADLINE_STYLE = {
  fontFamily: "Georgia, 'Times New Roman', serif",
};

function LeadStory({ story }: { story: HomepageStory }) {
  return (
    <article aria-label="Featured grocery news" className="border-b border-[#D8E3D9] py-5 sm:py-7">
      <Link
        href={bloggerUrlToNativeNewsPath(story.url)}
        className={"group grid items-center gap-4 sm:gap-6 md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-8 " + FOCUS_RING_CLASS}
      >
        <div className="relative aspect-[16/10] w-full overflow-hidden rounded-xl bg-[#E6EFE8] sm:aspect-[16/9] md:aspect-[4/3] lg:aspect-[3/2]">
          <NewsImage
            src={story.imageUrl}
            alt={story.imageAlt}
            sizes="(max-width: 767px) 100vw, (max-width: 1200px) 55vw, 650px"
            priority
            className="object-cover transition-transform duration-300 group-hover:scale-[1.025]"
          />
        </div>
        <div className="min-w-0 pb-1">
          <p className="font-ui text-[11px] font-bold uppercase tracking-[0.14em] text-[#307347]">
            Latest feature <span aria-hidden="true" className="mx-1 text-[#9EBAA4]">/</span> {story.categoryLabel}
          </p>
          <h2
            className="mt-2 font-display text-[29px] font-bold leading-[1.12] tracking-[-0.025em] text-[#173E2F] group-hover:underline group-hover:decoration-[#A5B9A8] group-hover:underline-offset-4 sm:text-[36px] lg:text-[42px]"
            style={EDITORIAL_HEADLINE_STYLE}
          >
            {story.title}
          </h2>
          {story.dek ? (
            <p className="mt-3 line-clamp-3 font-ui text-[14px] leading-[1.55] text-[#516455] sm:text-[16px]">{story.dek}</p>
          ) : null}
          <p className="mt-3 font-ui text-[12px] text-[#6D7E71]">
            <time dateTime={story.publishedAt}>{story.publishedLabel}</time>
            <span aria-hidden="true"> · </span>
            {story.readTimeMinutes} min read
          </p>
          <span className="mt-4 inline-flex items-center gap-1.5 font-ui text-[13px] font-bold text-[#24603B]">
            Read the story <span aria-hidden="true">→</span>
          </span>
        </div>
      </Link>
    </article>
  );
}

function EditorialStoryRow({ story }: { story: HomepageStory }) {
  return (
    <li className="border-b border-[#E2E9E2] last:border-b-0">
      <article>
        <Link
          href={bloggerUrlToNativeNewsPath(story.url)}
          className={"group grid grid-cols-[96px_minmax(0,1fr)] items-start gap-3 py-4 sm:grid-cols-[160px_minmax(0,1fr)] sm:gap-5 sm:py-5 " + FOCUS_RING_CLASS}
        >
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-[#E7EFE6]">
            <NewsImage
              src={story.imageUrl}
              alt={story.imageAlt}
              sizes="(max-width: 639px) 96px, 160px"
              className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            />
          </div>
          <div className="min-w-0">
            <p className="font-ui text-[10px] font-bold uppercase tracking-[0.10em] text-[#34734A] sm:text-[11px]">
              {story.categoryLabel}
            </p>
            <h3
              className="mt-1 line-clamp-3 text-[19px] font-bold leading-[1.2] text-[#183A2A] group-hover:underline group-hover:decoration-[#B8CBB8] sm:text-[23px]"
              style={EDITORIAL_HEADLINE_STYLE}
            >
              {story.title}
            </h3>
            {story.dek ? (
              <p className="mt-1.5 hidden line-clamp-2 font-ui text-[13px] leading-[1.5] text-[#596C5E] sm:block">{story.dek}</p>
            ) : null}
            <p className="mt-2 font-ui text-[11px] text-[#778479]">
              <time dateTime={story.publishedAt}>{story.publishedLabel}</time>
              <span aria-hidden="true"> · </span>
              {story.readTimeMinutes} min read
            </p>
          </div>
        </Link>
      </article>
    </li>
  );
}

export default function NewsArchive({ stories, activeCategory }: NewsArchiveProps) {
  const location = useShopperLocation();
  const flyers = useCurrentFlyers(location.zipcode);
  const [visibleCount, setVisibleCount] = useState(12);
  useEffect(() => { setVisibleCount(12); }, [activeCategory]);

  const activeLabel = activeCategory
    ? getHomepageCategory(activeCategory as HomepageStory["category"]).label
    : null;
  const leadStory = stories[0] ?? null;
  const visibleStories = stories.slice(1, visibleCount);
  // Keep secondary reading suggestions distinct from the first twelve articles.
  const suggestions = stories.slice(12, 15);
  const nextCount = Math.min(stories.length, visibleCount + 12);

  return (
    <>
      <Head>
        <title>{activeLabel ? activeLabel + " | " + PAGE_TITLE : PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <link rel="canonical" href={SITE_URL + "/news" + (activeCategory ? "?category=" + encodeURIComponent(activeCategory) : "")} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Youdle" />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={SITE_URL + "/news"} />
      </Head>
      <div
        className={manrope.variable + " " + inter.variable + " yd-home min-h-screen bg-[#FFFCF8] font-ui text-[#183A2A] antialiased"}
        style={fontVariables}
      >
        <HomeHeader />
        <main id="main-content">
          <div className="mx-auto w-full max-w-[1180px] px-4 pb-12 pt-5 sm:px-7 sm:pt-7 lg:px-9">
            <header className="border-b-2 border-[#173E2F] pb-5 sm:pb-6">
              <p className="font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-[#397E50]">
                The Youdle news desk
              </p>
              <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
                <div>
                  <h1 className="font-display text-[40px] font-extrabold leading-[1.02] tracking-[-0.045em] text-[#173E2F] sm:text-[53px] lg:text-[59px]">
                    <Link href="/news" className={"hover:underline hover:underline-offset-4 " + FOCUS_RING_CLASS}>Grocery News</Link>
                  </h1>
                  <p className="mt-2 max-w-[690px] font-ui text-[14px] leading-[1.5] text-[#566C5B] sm:text-[16px]">
                    Grocery news, trends, and topics to help you make smarter choices.
                  </p>
                </div>
                <Link
                  href="/newsletter"
                  className={"inline-flex min-h-[42px] shrink-0 items-center self-start rounded-full bg-[#173E2F] px-5 font-ui text-[13px] font-semibold text-white hover:bg-[#285F42] sm:self-auto " + FOCUS_RING_CLASS}
                >
                  Get the free newsletter <span aria-hidden="true" className="ml-2">→</span>
                </Link>
              </div>
            </header>

            <nav aria-label="Browse Grocery News topics" className="border-b border-[#D9E4D9]">
              <div className="-mx-1 flex items-center gap-1 overflow-x-auto px-1 py-2 sm:gap-2">
                <Link
                  href="/news"
                  aria-current={!activeCategory ? "page" : undefined}
                  className={
                    "inline-flex min-h-[39px] shrink-0 items-center whitespace-nowrap rounded-full px-3 font-ui text-[12px] font-semibold transition-colors " +
                    (!activeCategory ? "bg-[#173E2F] text-white" : "text-[#566D5B] hover:bg-[#EAF1E9] hover:text-[#173E2F]") +
                    " " + FOCUS_RING_CLASS
                  }
                >
                  All stories
                </Link>
                {HOMEPAGE_CATEGORIES.map(category => (
                  <Link
                    key={category.slug}
                    href={"/news?category=" + category.slug}
                    aria-current={activeCategory === category.slug ? "page" : undefined}
                    className={
                      "inline-flex min-h-[39px] shrink-0 items-center whitespace-nowrap rounded-full px-3 font-ui text-[12px] font-semibold transition-colors " +
                      (activeCategory === category.slug ? "bg-[#173E2F] text-white" : "text-[#566D5B] hover:bg-[#EAF1E9] hover:text-[#173E2F]") +
                      " " + FOCUS_RING_CLASS
                    }
                  >
                    {categoryLabels[category.slug] || category.label}
                  </Link>
                ))}
              </div>
            </nav>

            {leadStory ? <LeadStory story={leadStory} /> : null}

            <div className="grid gap-7 pt-6 lg:grid-cols-[minmax(0,1fr)_305px] lg:gap-10">
              <section aria-labelledby="grocery-news-latest" className="min-w-0">
                <div className="flex items-end justify-between gap-3 border-b-2 border-[#173E2F] pb-3">
                  <h2 id="grocery-news-latest" className="font-display text-[25px] font-bold leading-none tracking-tight text-[#173E2F] sm:text-[30px]">
                    {activeLabel ? "More in " + activeLabel : "The Latest"}
                  </h2>
                  <span className="shrink-0 font-ui text-[11px] text-[#68786B]">
                    {stories.length} {stories.length === 1 ? "story" : "stories"}
                  </span>
                </div>

                {visibleStories.length > 0 ? (
                  <>
                    <ol>
                      {visibleStories.map((story, index) => (
                        <Fragment key={story.id}>
                          <EditorialStoryRow story={story} />
                          {archiveFlyerAfter(index + 1) && flyers.length ? (
                            <li className="border-b border-[#E2E9E2] py-3">
                              <EditorialFlyerCard flyer={flyers[Math.floor((index + 1) / 5) % flyers.length]} />
                            </li>
                          ) : null}
                          {activeCategory === "recalls-warnings" && (index + 2) % 5 === 0 ? (
                            <li className="border-b border-[#E2E9E2] py-3">
                              <Link href="/specials" className={"inline-flex min-h-[44px] items-center rounded-xl border border-[#E5D4A5] bg-[#FFF8E9] px-4 text-[13px] font-bold text-[#36583F] " + FOCUS_RING_CLASS}>
                                Specials near you <span className="ml-2" aria-hidden="true">→</span>
                              </Link>
                            </li>
                          ) : null}
                        </Fragment>
                      ))}
                    </ol>
                    {visibleCount < stories.length ? (
                      <button
                        type="button"
                        className={"mt-5 inline-flex min-h-[44px] items-center justify-center rounded-full border border-[#B8D1BD] bg-white px-6 font-ui text-[14px] font-semibold text-[#285C3C] hover:bg-[#F4F9F3] " + FOCUS_RING_CLASS}
                        onClick={() => setVisibleCount(nextCount)}
                      >
                        Show more stories
                      </button>
                    ) : null}
                  </>
                ) : (
                  <div className="py-6">
                    <p className="font-ui text-sm font-semibold text-[#2E5F41]">
                      {leadStory ? "You're caught up on this topic." : "No stories in this topic yet."}
                    </p>
                    <Link href="/news" className={"mt-2 inline-flex min-h-[40px] items-center font-ui text-sm font-semibold text-[#28613D] underline " + FOCUS_RING_CLASS}>
                      Browse all Grocery News
                    </Link>
                  </div>
                )}

                <div className="mt-6 lg:hidden">
                  <NewsNewsletterCard placement="mobile" />
                </div>
              </section>

              <aside aria-label="More Grocery News" className="min-w-0">
                <div className="hidden lg:block">
                  <NewsNewsletterCard placement="sidebar" />
                </div>
                {suggestions.length > 0 ? (
                  <section aria-labelledby="grocery-news-more-reading" className="mt-6 border-t-2 border-[#173E2F] pt-3 lg:mt-7">
                    <h2 id="grocery-news-more-reading" className="font-display text-[22px] font-bold text-[#173E2F]">
                      Worth a look
                    </h2>
                    <ul className="mt-2 divide-y divide-[#DFE6DB]">
                      {suggestions.map(story => (
                        <li key={story.id}>
                          <Link
                            href={bloggerUrlToNativeNewsPath(story.url)}
                            className={"group grid grid-cols-[82px_minmax(0,1fr)] gap-3 py-3 " + FOCUS_RING_CLASS}
                          >
                            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-[#EEF4EC]">
                              <NewsImage
                                src={story.imageUrl}
                                alt={story.imageAlt}
                                sizes="82px"
                                className="object-cover transition-transform group-hover:scale-105"
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="font-ui text-[10px] font-bold uppercase tracking-[0.06em] text-[#408253]">{story.categoryLabel}</p>
                              <h3 className="mt-0.5 line-clamp-3 text-[16px] font-bold leading-[1.25] text-[#183A2A]" style={EDITORIAL_HEADLINE_STYLE}>
                                {story.title}
                              </h3>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                <Link
                  href="/post-your-flyer"
                  className={"mt-5 inline-flex min-h-[42px] items-center font-ui text-[13px] font-semibold text-[#275D3E] underline underline-offset-4 hover:text-[#173E2F] " + FOCUS_RING_CLASS}
                >
                  Post or build your flyer
                </Link>
              </aside>
            </div>
          </div>
        </main>
        <HomeFooter />
      </div>
    </>
  );
}

export const getServerSideProps: GetServerSideProps<NewsArchiveProps> = async ({
  query,
  res,
}) => {
  const requestedCategory =
    typeof query.category === "string" ? query.category : null;
  const activeCategory = HOMEPAGE_CATEGORIES.some(
    (category) => category.slug === requestedCategory,
  )
    ? requestedCategory
    : null;

  const records = await fetchBloggerPosts(50);
  const allStories = records
    .map((record) => toHomepageStory(record))
    .filter((story): story is HomepageStory => Boolean(story));

  const stories = activeCategory
    ? allStories.filter((story) =>
        story.category === activeCategory ||
        story.categories?.includes(activeCategory as HomepageStory["category"]),
      )
    : allStories;

  res.setHeader(
    "Cache-Control",
    "public, s-maxage=600, stale-while-revalidate=86400",
  );

  return {
    props: { stories, activeCategory },
  };
};
